import React, { createContext, useContext, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';

/**
 * Compatibility shim. The current user now comes from session auth (AuthContext).
 * `updateUser` / `updateAvatar` keep a local overlay so ProfilePage edits show
 * immediately; `fetchUser` re-hydrates from the server.
 */
const UserContext = createContext();

export function UserProvider({ children }) {
  const { user: authUser, loading, refresh } = useAuth();
  const [overlay, setOverlay] = useState(null);

  const value = useMemo(() => {
    const user = authUser ? { ...authUser, ...(overlay || {}) } : overlay;
    return {
      user,
      loading,
      error: null,
      fetchUser: refresh,
      updateUser: (u) => setOverlay((prev) => ({ ...(prev || {}), ...u })),
      updateAvatar: (avatarUrl) => setOverlay((prev) => ({ ...(prev || {}), avatarUrl })),
    };
  }, [authUser, overlay, loading, refresh]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
